import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Download } from "lucide-react";
import Avatar from "../../components/Avatar";
import PerformanceBadge from "../../components/PerformanceBadge";
import { StatChip, downloadCSV } from "./_shared";

const groupAvg = (arr) => arr.length
  ? +(arr.reduce((a, b) => a + b.overall_weighted_score, 0) / arr.length).toFixed(2)
  : 0;

export default function PerformanceReport({ home, classification, gender, month, year }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    const params = { month, year };
    if (home) params.home = home;
    api.get("/summary", { params }).then(({ data }) => setRows(data));
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [home, month, year]);

  const filtered = useMemo(() => rows.filter(r => {
    if (classification) {
      // Match either staff_classification (Direct/Contract) or access_role (House Manager/Administration)
      const matches = r.staff_classification === classification || r.access_role === classification;
      if (!matches) return false;
    }
    if (gender && r.gender !== gender) return false;
    return true;
  }), [rows, classification, gender]);

  const directAvg = groupAvg(filtered.filter(r => r.staff_classification === "Direct Care Worker"));
  const contractAvg = groupAvg(filtered.filter(r => r.staff_classification === "Contract Care Worker"));
  const overallAvg = groupAvg(filtered);

  const exportCSV = () => {
    const data = filtered.map(r => ({
      staff_name: r.staff_name, home: r.home,
      job_title: r.job_title || "",
      classification: r.staff_classification || r.access_role || "",
      gender: r.gender || "", month: r.month, year: r.year,
      overall_score: r.overall_weighted_score, performance_band: r.performance_band,
      quality: r.quality_average, reliability: r.reliability_average,
      documentation: r.documentation_average, health_safety: r.health_safety_average,
      professionalism: r.professionalism_average, compliance: r.compliance_average,
      manager_notes: r.manager_notes || "", recommendation: r.recommendation || "",
    }));
    downloadCSV(`nv-performance-${year}-${String(month).padStart(2,"0")}.csv`, data);
    toast.success("CSV downloaded");
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          <StatChip label="Staff Scored" value={filtered.length} tone="sky" />
          <StatChip label="Overall Avg" value={overallAvg.toFixed(2)} tone="emerald" />
          <StatChip label="Direct Avg" value={directAvg.toFixed(2)} tone="sky" />
          <StatChip label="Contract Avg" value={contractAvg.toFixed(2)} tone="purple" />
        </div>
        <button onClick={exportCSV} disabled={!filtered.length} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-sky-800 hover:bg-sky-900 text-white text-sm font-semibold disabled:opacity-50" data-testid="export-performance-csv">
          <Download size={14} /> Export CSV
        </button>
      </div>
      <div className="nv-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="performance-table">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="px-5 py-3 text-left">Staff</th>
              <th className="px-3 py-3 text-left">Home</th>
              <th className="px-3 py-3 text-left">Job Title</th>
              <th className="px-3 py-3 text-right">Quality</th>
              <th className="px-3 py-3 text-right">Reliab</th>
              <th className="px-3 py-3 text-right">Docs</th>
              <th className="px-3 py-3 text-right">H&amp;S</th>
              <th className="px-3 py-3 text-right">Prof</th>
              <th className="px-3 py-3 text-right">Comp</th>
              <th className="px-3 py-3 text-right">Overall</th>
              <th className="px-3 py-3 text-left">Band</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, idx) => (
              <tr key={`${r.staff_user_id || r.staff_name}-${r.home}-${r.month}-${r.year}-${idx}`} className="border-t border-slate-100">
                <td className="px-5 py-3"><div className="flex items-center gap-2.5"><Avatar name={r.staff_name} gender={r.gender} src={r.profile_image} size="sm" /><span className="font-medium text-slate-800">{r.staff_name}</span></div></td>
                <td className="px-3 py-3 text-slate-600">{r.home}</td>
                <td className="px-3 py-3 text-slate-600">{r.job_title || r.staff_classification || "—"}</td>
                <td className="px-3 py-3 text-right tabular-nums">{r.quality_average?.toFixed(2)}</td>
                <td className="px-3 py-3 text-right tabular-nums">{r.reliability_average?.toFixed(2)}</td>
                <td className="px-3 py-3 text-right tabular-nums">{r.documentation_average?.toFixed(2)}</td>
                <td className="px-3 py-3 text-right tabular-nums">{r.health_safety_average?.toFixed(2)}</td>
                <td className="px-3 py-3 text-right tabular-nums">{r.professionalism_average?.toFixed(2)}</td>
                <td className="px-3 py-3 text-right tabular-nums">{r.compliance_average?.toFixed(2)}</td>
                <td className="px-3 py-3 text-right font-bold tabular-nums">{r.overall_weighted_score?.toFixed(2)}</td>
                <td className="px-3 py-3"><PerformanceBadge band={r.performance_band} /></td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={11} className="px-5 py-10 text-center text-slate-500">No performance data for this period.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
